import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

export type TimerState = 'stopped' | 'running' | 'paused' | 'finished';

interface TimerContextType {
  // Timer state
  state: TimerState;
  timeLeft: number;
  initialTime: number;
  
  // Timer controls
  startTimer: (seconds?: number) => void;
  pauseTimer: () => void;
  resumeTimer: () => void;
  resetTimer: () => void;
  stopTimer: () => void;
  configureTimer: (seconds: number) => void;
  restartTimer: () => void;
  
  // Helper functions
  isActive: boolean;
  formatTime: (seconds: number) => string;
  
  // Notification functions
  requestNotificationPermission: () => Promise<boolean>;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export const TimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<TimerState>('stopped');
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [initialTime, setInitialTime] = useState<number>(0);
  
  // Refs to track timer state for background operation
  const timerStartTime = useRef<number | null>(null);
  const accumulatedPausedTime = useRef<number>(0);
  const lastPauseTime = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);

  // Request notification permission
  const requestNotificationPermission = useCallback(async (): Promise<boolean> => {
    if (!('Notification' in window)) {
      console.log('This browser does not support desktop notifications');
      return false;
    }
    
    if (Notification.permission === 'granted') {
      return true;
    }
    
    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    
    return false;
  }, []);

  // Update timer display and handle state transitions
  const updateTimer = useCallback(() => {
    if (state === 'running' && timerStartTime.current !== null) {
      const now = performance.now();
      const elapsedTime = (now - timerStartTime.current - accumulatedPausedTime.current) / 1000;
      const remaining = Math.max(0, initialTime - elapsedTime);
      const newTimeLeft = Math.ceil(remaining);
      
      setTimeLeft(newTimeLeft);
      
      if (newTimeLeft <= 0) {
        setState('finished');
        timerStartTime.current = null;
        accumulatedPausedTime.current = 0;
        
        // Trigger notification when timer finishes
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Timer Finished!', {
            body: 'Your rest time is up!',
            icon: '/favicon.ico',
            tag: 'gym-timer',
            requireInteraction: true
          });
        }
      }
    }
  }, [state, initialTime]);

  // Handle page visibility change (for iOS background/foreground)
  const handleVisibilityChange = useCallback(() => {
    if (state === 'running') {
      if (document.hidden) {
        // Page is hidden - we don't need to do anything special since we're using performance.now()
        console.log('Timer: Page hidden, timer continues in background');
      } else {
        // Page is visible again - recalculate time left
        console.log('Timer: Page visible, recalculating time');
        updateTimer();
      }
    }
  }, [state, updateTimer]);

  // Set up page visibility listener
  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  // Main timer effect
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    if (state === 'running') {
      // Update immediately when starting
      updateTimer();
      
      // Set up interval for regular updates
      intervalRef.current = window.setInterval(() => {
        updateTimer();
      }, 1000);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [state, updateTimer]);

  const startTimer = useCallback(async (seconds?: number) => {
    // Request notification permission when starting timer
    await requestNotificationPermission();
    
    const timerDuration = seconds || 90; // Default to 90 seconds
    setInitialTime(timerDuration);
    setTimeLeft(timerDuration);
    
    // Initialize timing references
    timerStartTime.current = performance.now();
    accumulatedPausedTime.current = 0;
    lastPauseTime.current = null;
    
    setState('running');
  }, [requestNotificationPermission]);

  const pauseTimer = useCallback(() => {
    if (state === 'running') {
      setState('paused');
      lastPauseTime.current = performance.now();
    }
  }, [state]);

  const resumeTimer = useCallback(() => {
    if (state === 'paused') {
      // Add the paused time to accumulated paused time if we have a pause time
      if (lastPauseTime.current !== null) {
        const pauseDuration = performance.now() - lastPauseTime.current;
        accumulatedPausedTime.current += pauseDuration;
      }
      lastPauseTime.current = null;
      
      // Manually update the timer to ensure correct time calculation before state change
      if (timerStartTime.current !== null) {
        const now = performance.now();
        const elapsedTime = (now - timerStartTime.current - accumulatedPausedTime.current) / 1000;
        const remaining = Math.max(0, initialTime - elapsedTime);
        const newTimeLeft = Math.ceil(remaining);
        setTimeLeft(newTimeLeft);
      }
      
      setState('running');
    }
  }, [state, initialTime]);

  const resetTimer = useCallback(() => {
    setTimeLeft(initialTime);
    setState('paused');
    
    // Reset timing references
    timerStartTime.current = performance.now();
    accumulatedPausedTime.current = 0;
    lastPauseTime.current = performance.now(); // Start in paused state
  }, [initialTime]);

  const stopTimer = useCallback(() => {
    setState('stopped');
    setTimeLeft(0);
    setInitialTime(0);
    
    // Clear timing references
    timerStartTime.current = null;
    accumulatedPausedTime.current = 0;
    lastPauseTime.current = null;
  }, []);

  const restartTimer = useCallback(() => {
    setTimeLeft(initialTime);
    
    // Reset timing references for restart
    timerStartTime.current = performance.now();
    accumulatedPausedTime.current = 0;
    lastPauseTime.current = null;
    
    setState('running');
  }, [initialTime]);

  const configureTimer = useCallback((seconds: number) => {
    setInitialTime(seconds);
    setTimeLeft(seconds);
    if (state === 'stopped') {
      // Initialize timing references
      timerStartTime.current = performance.now();
      accumulatedPausedTime.current = 0;
      lastPauseTime.current = null;
      setState('running');
    } else {
      // Reconfigure existing timer
      timerStartTime.current = performance.now();
      accumulatedPausedTime.current = 0;
      if (state === 'paused') {
        lastPauseTime.current = performance.now();
      }
    }
  }, [state]);

  const formatTime = useCallback((seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  const isActive = state !== 'stopped';

  const value = {
    state,
    timeLeft,
    initialTime,
    startTimer,
    pauseTimer,
    resumeTimer,
    resetTimer,
    stopTimer,
    configureTimer,
    restartTimer,
    isActive,
    formatTime,
    requestNotificationPermission
  };

  return (
    <TimerContext.Provider value={value}>
      {children}
    </TimerContext.Provider>
  );
};

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (context === undefined) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
}; 